import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  buildIntervalGroups,
  intervalKeyFromPhoto,
  inferCondition,
  enumerateFullSizeUrlCandidates,
} from '../utils/corePhotoCondition';
import { logCorePhotoImageError, logCorePhotoImageExhausted } from '../utils/corePhotoDebug';
import './CorePhotosGallery.css';

const MIN_SCALE = 1;
const MAX_SCALE = 8;
const WHEEL_ZOOM_FACTOR = 1.12;
const DOUBLE_CLICK_ZOOM = 2;

function clamp(n, lo, hi) {
  return Math.min(hi, Math.max(lo, n));
}

/**
 * Zoom toward a point in viewport coords (relative to viewport top-left).
 * transform: translate(tx,ty) scale(s) with origin 0 0.
 */
function zoomAtPoint(prev, nextScale, px, py) {
  const s = clamp(nextScale, MIN_SCALE, MAX_SCALE);
  if (s <= MIN_SCALE + 1e-6) {
    return { scale: MIN_SCALE, tx: 0, ty: 0 };
  }
  const ratio = s / prev.scale;
  return {
    scale: s,
    tx: px - ratio * (px - prev.tx),
    ty: py - ratio * (py - prev.ty),
  };
}

/** Full-screen image modal for core tray photos — original/preview SAS, arrows, ESC, wet/dry pair, zoom. */
function CorePhotoLightbox({ photos, initialIndex = 0, onClose, isOpen }) {
  const groups = useMemo(() => buildIntervalGroups(photos), [photos]);

  const [index, setIndex] = useState(initialIndex);
  const [wetDryPick, setWetDryPick] = useState(null); // null = show photos[index]; 'wet' | 'dry' forces that variant when available

  const [zoom, setZoom] = useState({ scale: 1, tx: 0, ty: 0 });
  const [dragging, setDragging] = useState(false);
  const viewportRef = useRef(null);
  const dragRef = useRef(null); // { pointerId, startX, startY, originTx, originTy }

  const resetZoom = useCallback(() => {
    setZoom({ scale: 1, tx: 0, ty: 0 });
    setDragging(false);
    dragRef.current = null;
  }, []);

  useEffect(() => {
    setIndex(initialIndex);
    setWetDryPick(null);
    resetZoom();
  }, [initialIndex, resetZoom]);

  useEffect(() => {
    if (!isOpen) {
      resetZoom();
      return undefined;
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setIndex((i) => Math.max(0, i - 1));
        setWetDryPick(null);
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setIndex((i) => Math.min(Math.max(0, photos.length - 1), i + 1));
        setWetDryPick(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose, photos.length, resetZoom]);

  const basePhoto = photos[index];
  const key = basePhoto ? intervalKeyFromPhoto(basePhoto) : null;
  const group = key ? groups.get(key) : null;
  const canWetDry = Boolean(group?.wet?.length && group?.dry?.length);

  const displayedPhoto = useMemo(() => {
    if (!basePhoto) return null;
    if (wetDryPick === 'wet' && group?.wet?.[0]) return group.wet[0];
    if (wetDryPick === 'dry' && group?.dry?.[0]) return group.dry[0];
    return basePhoto;
  }, [basePhoto, group, wetDryPick]);

  const fullUrlCandidates = useMemo(
    () => (displayedPhoto ? enumerateFullSizeUrlCandidates(displayedPhoto) : []),
    [displayedPhoto],
  );

  /** Which candidate URL we're showing when previous SAS URLs 404 */
  const [fullUrlAttempt, setFullUrlAttempt] = useState(0);
  const [fullUrlExhausted, setFullUrlExhausted] = useState(false);

  useEffect(() => {
    setFullUrlAttempt(0);
    setFullUrlExhausted(false);
    resetZoom();
  }, [displayedPhoto?.id, displayedPhoto?.blob_path, wetDryPick, index, resetZoom]);

  const fullSrc = fullUrlCandidates[fullUrlAttempt] ?? null;
  const depthLabel =
    displayedPhoto?.depth_from_m != null && displayedPhoto?.depth_to_m != null
      ? `${Number(displayedPhoto.depth_from_m).toFixed(2)}–${Number(displayedPhoto.depth_to_m).toFixed(2)} m`
      : displayedPhoto?.file_name || '';

  // Non-passive wheel so we can preventDefault (page/backdrop scroll)
  useEffect(() => {
    if (!isOpen || !fullSrc) return undefined;
    const el = viewportRef.current;
    if (!el) return undefined;

    const onWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const rect = el.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      const factor = e.deltaY < 0 ? WHEEL_ZOOM_FACTOR : 1 / WHEEL_ZOOM_FACTOR;
      setZoom((prev) => zoomAtPoint(prev, prev.scale * factor, px, py));
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [isOpen, fullSrc]);

  const goPrev = useCallback(() => {
    setIndex((i) => Math.max(0, i - 1));
    setWetDryPick(null);
  }, []);

  const goNext = useCallback(() => {
    setIndex((i) => Math.min(Math.max(0, photos.length - 1), i + 1));
    setWetDryPick(null);
  }, [photos.length]);

  const onPointerDown = useCallback(
    (e) => {
      if (zoom.scale <= MIN_SCALE + 1e-6) return;
      if (e.button !== 0) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      dragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        originTx: zoom.tx,
        originTy: zoom.ty,
      };
      setDragging(true);
    },
    [zoom.scale, zoom.tx, zoom.ty],
  );

  const onPointerMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    setZoom((prev) => ({
      ...prev,
      tx: d.originTx + (e.clientX - d.startX),
      ty: d.originTy + (e.clientY - d.startY),
    }));
  }, []);

  const endDrag = useCallback((e) => {
    const d = dragRef.current;
    if (!d || (e && d.pointerId !== e.pointerId)) return;
    dragRef.current = null;
    setDragging(false);
  }, []);

  const onDoubleClick = useCallback((e) => {
    const el = viewportRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    setZoom((prev) => {
      if (prev.scale > MIN_SCALE + 0.05) {
        return { scale: MIN_SCALE, tx: 0, ty: 0 };
      }
      return zoomAtPoint(prev, DOUBLE_CLICK_ZOOM, px, py);
    });
  }, []);

  if (!isOpen || typeof document === 'undefined') return null;

  const zoomed = zoom.scale > MIN_SCALE + 1e-6;
  const viewportCursor = dragging ? 'grabbing' : zoomed ? 'grab' : 'zoom-in';

  return createPortal(
    <div
      className="core-photo-lightbox-root"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 32000,
        background: 'rgba(0,0,0,0.88)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Core photo"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="core-photo-lightbox-inner" style={{ maxWidth: '96vw', maxHeight: '96vh', position: 'relative' }}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose?.();
          }}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: -36,
            right: 0,
            background: 'transparent',
            border: 'none',
            color: '#e5e7eb',
            fontSize: '1.75rem',
            cursor: 'pointer',
            lineHeight: 1,
          }}
        >
          ×
        </button>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem', marginBottom: '0.65rem' }}>
          <span style={{ color: '#f9fafb', fontWeight: 600 }}>{photos[index]?.hole_id}</span>
          <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>{depthLabel}</span>
          {displayedPhoto && (
            <span style={{ color: '#64748b', fontSize: '0.78rem', textTransform: 'capitalize' }}>
              ({inferCondition(displayedPhoto)})
            </span>
          )}
          {zoomed && (
            <span style={{ color: '#6b7280', fontSize: '0.72rem' }}>
              {zoom.scale.toFixed(1)}× · scroll to zoom · drag to pan
            </span>
          )}
        </div>

        {canWetDry && (
          <div style={{ marginBottom: '0.65rem', display: 'flex', gap: '0.35rem' }}>
            <button
              type="button"
              onClick={() => setWetDryPick(null)}
              style={{
                padding: '0.25rem 0.65rem',
                borderRadius: 6,
                border: wetDryPick == null ? '1px solid #f9be0b' : '1px solid #4b5563',
                background: wetDryPick == null ? 'rgba(249,190,11,0.15)' : '#1f2937',
                color: '#e5e7eb',
                cursor: 'pointer',
                fontSize: '0.75rem',
              }}
            >
              As listed
            </button>
            <button
              type="button"
              onClick={() => setWetDryPick('wet')}
              style={{
                padding: '0.25rem 0.65rem',
                borderRadius: 6,
                border: wetDryPick === 'wet' ? '1px solid #38bdf8' : '1px solid #4b5563',
                background: wetDryPick === 'wet' ? 'rgba(56,189,248,0.12)' : '#1f2937',
                color: '#e5e7eb',
                cursor: 'pointer',
                fontSize: '0.75rem',
              }}
            >
              Wet
            </button>
            <button
              type="button"
              onClick={() => setWetDryPick('dry')}
              style={{
                padding: '0.25rem 0.65rem',
                borderRadius: 6,
                border: wetDryPick === 'dry' ? '1px solid #a78bfa' : '1px solid #4b5563',
                background: wetDryPick === 'dry' ? 'rgba(167,139,250,0.12)' : '#1f2937',
                color: '#e5e7eb',
                cursor: 'pointer',
                fontSize: '0.75rem',
              }}
            >
              Dry
            </button>
          </div>
        )}

        {fullSrc && !fullUrlExhausted ? (
          <div
            ref={viewportRef}
            className="core-photo-lightbox-viewport"
            style={{ cursor: viewportCursor }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onDoubleClick={onDoubleClick}
          >
            <img
              className="core-photo-lightbox-img"
              alt={depthLabel ? `Core tray image, ${depthLabel}` : 'Core tray image'}
              src={fullSrc}
              draggable={false}
              style={{
                transform: `translate(${zoom.tx}px, ${zoom.ty}px) scale(${zoom.scale})`,
                transformOrigin: '0 0',
              }}
              onError={() => {
                setFullUrlAttempt((a) => {
                  logCorePhotoImageError('lightbox', displayedPhoto, {
                    attempt: a,
                    totalCandidates: fullUrlCandidates.length,
                    failedUrl: fullUrlCandidates[a],
                  });
                  const next = a + 1;
                  if (next < fullUrlCandidates.length) return next;
                  queueMicrotask(() => {
                    setFullUrlExhausted(true);
                    logCorePhotoImageExhausted('lightbox', displayedPhoto, fullUrlCandidates);
                  });
                  return a;
                });
              }}
            />
          </div>
        ) : fullUrlExhausted ? (
          <div style={{ color: '#fca5a5', padding: '2rem' }}>
            Could not load this image. All candidate blob URLs failed.
          </div>
        ) : (
          <div style={{ color: '#9ca3af', padding: '2rem' }}>No URL for this asset.</div>
        )}

        {photos.length > 1 && (
          <div
            style={{
              marginTop: '0.85rem',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '1rem',
            }}
          >
            <button
              type="button"
              disabled={index <= 0}
              onClick={goPrev}
              style={{
                padding: '0.5rem 0.95rem',
                borderRadius: 6,
                border: '1px solid #374151',
                background: '#111827',
                color: index <= 0 ? '#6b7280' : '#f9fafb',
                cursor: index <= 0 ? 'default' : 'pointer',
              }}
            >
              ← Prev
            </button>
            <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
              {index + 1} / {photos.length}
            </span>
            <button
              type="button"
              disabled={index >= photos.length - 1}
              onClick={goNext}
              style={{
                padding: '0.5rem 0.95rem',
                borderRadius: 6,
                border: '1px solid #374151',
                background: '#111827',
                color: index >= photos.length - 1 ? '#6b7280' : '#f9fafb',
                cursor: index >= photos.length - 1 ? 'default' : 'pointer',
              }}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default CorePhotoLightbox;
